import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useCookieConsent } from '../contexts/CookieConsentContext/CookieConsentContext';

const META_PIXEL_ID = '1440139234173742';

/**
 * Hook to initialize Meta Pixel and track page views
 * Implements Meta's consent API for GDPR compliance
 */
export const useMetaPixel = () => {
  const { marketingAllowed, isLoaded, hasConsent } = useCookieConsent();
  const location = useLocation();
  const isInitialized = useRef(false);
  const lastTrackedPath = useRef(null);

  // Initialize Meta Pixel when consent is given
  useEffect(() => {
    if (!isLoaded || !marketingAllowed || isInitialized.current) {
      return;
    }

    // Check if fbq is already loaded
    if (window.fbq) {
      // If already loaded but consent was revoked and now granted again
      window.fbq('consent', 'grant');
      isInitialized.current = true;
      return;
    }

    // Load Meta Pixel script dynamically
    const fbq = (window.fbq = function () {
      fbq.callMethod ? fbq.callMethod.apply(fbq, arguments) : fbq.queue.push(arguments);
    });

    if (!window._fbq) window._fbq = fbq;
    fbq.push = fbq;
    fbq.loaded = true;
    fbq.version = '2.0';
    fbq.queue = [];

    const script = document.createElement('script');
    script.async = true;
    script.src = 'https://connect.facebook.net/en_US/fbevents.js';
    script.onerror = () => {
      console.warn('Failed to load Meta Pixel script');
      isInitialized.current = false;
    };
    document.head.appendChild(script);

    // Grant consent before init since user already consented
    window.fbq('consent', 'grant');
    window.fbq('init', META_PIXEL_ID);

    isInitialized.current = true;
  }, [isLoaded, marketingAllowed]);

  // Handle consent revocation (user changes preferences)
  useEffect(() => {
    if (!isLoaded || !hasConsent || !window.fbq) return;

    if (!marketingAllowed && isInitialized.current) {
      window.fbq('consent', 'revoke');
      isInitialized.current = false;
    }
  }, [isLoaded, hasConsent, marketingAllowed]);

  // Track page views on route change
  useEffect(() => {
    if (!isInitialized.current || !marketingAllowed) {
      return;
    }

    if (lastTrackedPath.current === location.pathname) {
      return;
    }

    lastTrackedPath.current = location.pathname;
    trackMetaPageView();
  }, [location.pathname, marketingAllowed]);

  return {
    isInitialized: isInitialized.current,
    trackMetaEvent,
    trackMetaPageView,
  };
};

/**
 * Track a page view
 */
const trackMetaPageView = () => {
  if (!window.fbq) return;
  window.fbq('track', 'PageView');
};

/**
 * Track a custom event
 * @param {string} eventName - Event name (e.g., 'Lead', 'Contact', 'ViewContent')
 * @param {Object} params - Event parameters
 */
const trackMetaEvent = (eventName, params = {}) => {
  if (!window.fbq) return;
  window.fbq('track', eventName, params);
};

export { trackMetaEvent, trackMetaPageView };
